/**
 * Class Grade Breakdown API Contract (SCH-1694)
 *
 * GET /v1/schools/:school_public_id/school-users/:user_public_id/sections/:section_public_id/grade-breakdown
 *
 * One student's grade picture for one class — what Blackbaud's Grade detail page shows:
 * the class percentage as Blackbaud reported it, the teachers, the four status counts, and
 * per assignment type the count, points and Blackbaud's own category percentage.
 *
 * SERVER-GATED (SCH-1783): the school's `globalFeatures.classGrades` block selects the
 * audience (`students`, `parents`), and the parent audience also requires Blackbaud
 * list-as-parent and parental access. Returned only to the student when students are on,
 * and to such a parent when parents are on. Every other viewer, and every id that does not
 * resolve to one of the student's own classes at that school, gets the same 404.
 *
 * Numbers are Blackbaud's, never recomputed: `headline.percent` is the Grade Snapshot and
 * `percentage` on each category is the value Blackbaud sent. Counts and points sum the
 * rows we mirrored, so they can undercount Blackbaud where the sync skipped a row.
 */

import { z } from 'zod'

/** One assignment inside a category. */
export const ClassGradeBreakdownAssignmentSchema = z.object({
  /** Assignment public id. */
  id: z.string(),
  name: z.string().nullable(),
  /** ISO timestamp, or null when Blackbaud set no due date. */
  dateDue: z.string().nullable(),
  /** Points the student earned. Null when ungraded; a real 0 is a grade. */
  points: z.number().nullable(),
  maxPoints: z.number().nullable(),
  /** The assignment's own letter where the school grades that way, else null. */
  letter: z.string().nullable(),
  isExempt: z.boolean(),
  isIncomplete: z.boolean(),
  isLate: z.boolean(),
  isMissing: z.boolean()
})

export type ClassGradeBreakdownAssignment = z.infer<typeof ClassGradeBreakdownAssignmentSchema>

/**
 * One assignment type (Blackbaud's "category") with its assignments.
 *
 * `typeId` and `name` are null for the one group of assignments the graded sync has not
 * typed yet (typically work not graded so far); every other group carries both.
 */
export const ClassGradeBreakdownCategorySchema = z.object({
  typeId: z.string().nullable(),
  name: z.string().nullable(),
  /** The type's weight in the class, from its most recent graded write. Null in points-based gradebooks. */
  weight: z.number().nullable(),
  /** Assignments with points that are not exempt — the rows `pointsEarned` and `pointsPossible` sum. */
  gradedCount: z.number().int().nonnegative(),
  pointsEarned: z.number(),
  pointsPossible: z.number(),
  /**
   * Blackbaud's own percentage for this type, e.g. 92.56. Null until the graded sync has
   * stored one, and null whenever `breakdown.asOf` is null or more than 7 calendar days old.
   */
  percentage: z.number().nullable(),
  assignments: z.array(ClassGradeBreakdownAssignmentSchema)
})

export type ClassGradeBreakdownCategory = z.infer<typeof ClassGradeBreakdownCategorySchema>

/** How many of the student's live, visible assignments in the class carry each flag. */
export const ClassGradeBreakdownStatusCountsSchema = z.object({
  exempt: z.number().int().nonnegative(),
  incomplete: z.number().int().nonnegative(),
  late: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative()
})

export type ClassGradeBreakdownStatusCounts = z.infer<typeof ClassGradeBreakdownStatusCountsSchema>

/**
 * The class percentage: the latest Grade Snapshot for the class within the last 7
 * calendar days, whatever marking period it carries. Null when there is none.
 */
export const ClassGradeBreakdownHeadlineSchema = z.object({
  percent: z.number(),
  markingPeriodId: z.string().nullable(),
  /** The day the snapshot was captured, as an ISO timestamp at UTC midnight. */
  capturedDate: z.string()
})

export type ClassGradeBreakdownHeadline = z.infer<typeof ClassGradeBreakdownHeadlineSchema>

export const ClassGradeBreakdownSectionSchema = z.object({
  /** The lead section public id — the id the Classes list hands the app. */
  id: z.string(),
  /** The public id of the section whose rows were read (a sub-section of the lead, or the lead itself). */
  resolvedSectionId: z.string(),
  name: z.string(),
  term: z
    .object({
      name: z.string().nullable(),
      beginDate: z.string(),
      endDate: z.string()
    })
    .nullable()
})

export type ClassGradeBreakdownSection = z.infer<typeof ClassGradeBreakdownSectionSchema>

export const ClassGradeBreakdownTeacherSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable()
})

export type ClassGradeBreakdownTeacher = z.infer<typeof ClassGradeBreakdownTeacherSchema>

export const ClassGradeBreakdownDetailSchema = z.object({
  /**
   * When the graded-assignment sync last wrote any of the student's rows for this class
   * (ISO timestamp). Null when it never has, so a school whose graded sync is off does
   * not read as fresh.
   */
  asOf: z.string().nullable(),
  /** The marking period most of the graded rows carry. Null when none carries one. */
  markingPeriodId: z.string().nullable(),
  /**
   * Whether the headline and the breakdown describe the same marking period. Null when
   * either side has no marking period. After a marking-period close the two can differ.
   */
  sameMarkingPeriod: z.boolean().nullable(),
  statusCounts: ClassGradeBreakdownStatusCountsSchema,
  categories: z.array(ClassGradeBreakdownCategorySchema)
})

export type ClassGradeBreakdownDetail = z.infer<typeof ClassGradeBreakdownDetailSchema>

export const ClassGradeBreakdownDataSchema = z.object({
  student: z.object({
    /** The student's `UsersOnSchools` public id. */
    id: z.string()
  }),
  section: ClassGradeBreakdownSectionSchema,
  teachers: z.array(ClassGradeBreakdownTeacherSchema),
  headline: ClassGradeBreakdownHeadlineSchema.nullable(),
  /** Always null: Blackbaud exposes no class-level letter and no grade scale. */
  classLetter: z.null(),
  breakdown: ClassGradeBreakdownDetailSchema
})

export type ClassGradeBreakdownData = z.infer<typeof ClassGradeBreakdownDataSchema>

export const GetClassGradeBreakdownResponseSchema = z.object({
  message: z.string(),
  data: ClassGradeBreakdownDataSchema
})

export type GetClassGradeBreakdownResponse = z.infer<typeof GetClassGradeBreakdownResponseSchema>
